<template>
  <div class="mt-8">
    <h2 class="text-2xl font-bold text-white mb-6">
      Ocene korisnika {{ username }}
    </h2>

    <!-- Auth required message -->
    <div
      v-if="authRequired"
      class="bg-gray-800 rounded-lg p-8 text-center text-gray-400 mb-6"
    >
      <div class="flex flex-col items-center">
        <svg
          class="h-16 w-16 text-gray-500 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <p class="text-lg mb-4">
          Morate biti ulogovani da biste videli ocene korisnika.
        </p>
        <router-link
          to="/login"
          class="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Prijavite se
        </router-link>
      </div>
    </div>

    <!-- Rating stats -->
    <ProfileRatingStats
      v-if="!authRequired"
      :stats="stats"
      :userRole="userRole"
      :activeFilter="ratingFilter"
      @change-role="(role) => $emit('change-role', role)"
      @change-filter="(filter) => $emit('change-filter', filter)"
    />

    <!-- Ratings content container with fixed minimum height -->
    <div v-if="!authRequired" class="min-h-[600px] flex flex-col">
      <!-- Loader for ratings -->
      <div
        v-if="showLoader"
        class="flex justify-center items-center py-20 flex-grow"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Ratings list -->
      <div
        v-else-if="!loading && ratings.length > 0"
        class="space-y-4 flex-grow"
      >
        <div
          v-for="rating in ratings"
          :key="rating.id"
          class="bg-gray-800 rounded-lg p-6 shadow-md"
        >
          <div class="flex flex-col md:flex-row md:items-start">
            <div class="flex-shrink-0 mb-4 md:mb-0 md:mr-4">
              <div
                class="bg-gray-700 rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold text-blue-400"
              >
                {{ rating.fromUser.username.charAt(0).toUpperCase() }}
              </div>
            </div>
            <div class="flex-grow">
              <div
                class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3"
              >
                <h3 class="text-lg font-medium text-white">
                  {{ rating.fromUser.username }}
                </h3>
                <div class="flex items-center">
                  <div
                    class="px-3 py-1 rounded-full text-sm font-medium"
                    :class="{
                      'bg-green-900 text-green-300': rating.isLike,
                      'bg-red-900 text-red-300': !rating.isLike,
                    }"
                  >
                    {{ rating.isLike ? 'Pozitivna' : 'Negativna' }}
                  </div>
                </div>
              </div>
              <p class="text-gray-300">{{ rating.comment }}</p>
              <div v-if="rating.reply" class="mt-3 p-3 bg-gray-700 rounded-md">
                <p class="text-gray-400 text-sm mb-1">Odgovor:</p>
                <p class="text-gray-300">{{ rating.reply }}</p>
              </div>
              <div
                class="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mt-3 text-sm text-gray-500"
              >
                <span>{{ formatDate(rating.createdAt) }}</span>
                <router-link
                  :to="{
                    name: 'product-detail',
                    params: { id: rating.product.slug },
                  }"
                  class="text-blue-400 hover:underline flex items-center"
                >
                  <svg
                    class="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {{ rating.product.name }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No ratings -->
      <div
        v-else-if="!loading"
        class="bg-gray-800 rounded-lg p-8 text-center text-gray-400 mt-6 flex-grow flex items-center justify-center"
      >
        <div>
          <p v-if="ratingFilter !== 'all'">
            Korisnik nema
            {{ ratingFilter === 'positive' ? 'pozitivnih' : 'negativnih' }}
            ocena kao {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}.
          </p>
          <p v-else>
            Korisnik nema ocena kao
            {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}.
          </p>
        </div>
      </div>
    </div>

    <!-- Ratings pagination - outside the content container -->
    <div v-if="!authRequired" class="mt-8">
      <PaginationComponent
        :meta="meta"
        :current-page="currentPage"
        @page-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, watch, ref, onBeforeUnmount } from 'vue';
import ProfileRatingStats from './ProfileRatingStats.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import { debounce } from '@/utils/debounce';

interface Rating {
  id: string;
  fromUserId: string;
  toUserId: string;
  isLike: boolean;
  comment: string;
  reply: string | null;
  productId: string;
  ratingType: 'seller' | 'buyer';
  createdAt: string;
  updatedAt: string;
  fromUser: {
    id: string;
    username: string;
  };
  product: {
    id: string;
    name: string;
    slug: string;
  };
}

interface RatingStats {
  seller: {
    likes: number;
    dislikes: number;
  };
  buyer: {
    likes: number;
    dislikes: number;
  };
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default defineComponent({
  name: 'ProfileRatings',
  components: {
    ProfileRatingStats,
    PaginationComponent,
  },
  props: {
    username: {
      type: String,
      required: true,
    },
    ratings: {
      type: Array as PropType<Rating[]>,
      default: () => [],
    },
    stats: {
      type: Object as PropType<RatingStats>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    meta: {
      type: Object as PropType<Meta>,
      required: true,
    },
    userRole: {
      type: String as PropType<'seller' | 'buyer'>,
      default: 'seller',
    },
    ratingFilter: {
      type: String as PropType<'all' | 'positive' | 'negative'>,
      default: 'all',
    },
    authRequired: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change-role', 'change-page', 'change-filter'],
  setup(props, { emit }) {
    const showLoader = ref(false);

    // Debounced loader functions
    const debouncedShowLoader = debounce(() => {
      showLoader.value = true;
    }, 300);

    const hideLoaderImmediately = () => {
      debouncedShowLoader.cancel();
      showLoader.value = false;
    };

    // Watch loading state for debounced spinner
    watch(
      () => props.loading,
      (isLoading) => {
        if (isLoading) {
          debouncedShowLoader();
        } else {
          hideLoaderImmediately();
        }
      }
    );

    const formatDate = (dateString: string): string => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('sr-RS', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    };

    // Debounced page change function
    const debouncedPageChange = debounce((page: number) => {
      emit('change-page', page);
    }, 200);

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      showLoader,
      formatDate,
      handlePageChange: debouncedPageChange,
    };
  },
});
</script>
